import { ReturnComponent } from '@/utils/reactTypes';
import { useEffect } from 'react';
import { ScrollRestoration, createBrowserRouter, useLocation } from 'react-router';

type DataRouter = ReturnType<typeof createBrowserRouter>;

type AppScrollRestorationProps = {
    router: DataRouter;
};

// Stop re-applying once the document has plausibly finished growing, so a page that never
// gets tall enough doesn't keep an animation frame loop alive.
const RESTORE_DEADLINE_MS = 5000;

// Events that mean the reader has taken over; a restore after this point would yank the
// viewport out from under them.
const USER_SCROLL_EVENTS = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;

const maxScrollTop = (): number =>
    document.documentElement.scrollHeight - document.documentElement.clientHeight;

/**
 * Re-applies the offset the router restores when a navigation lands on a history entry
 * with a saved position, until the document is tall enough to hold it. Navigations to a
 * new entry have no saved position and leave the router's reset to the top alone.
 *
 * Pages fetch through Apollo rather than router loaders, so at the moment the router
 * restores an offset the route is still a suspense fallback and the document is short. The
 * browser clamps the scroll to that short document, and the router's restore is a one-shot
 * layout effect that never retries once the content arrives.
 */
export const useDeferredScrollRestore = (router: DataRouter): void => {
    const { key } = useLocation();

    useEffect(() => {
        const target = router.state.restoreScrollPosition;
        if (typeof target !== 'number' || target <= 0 || maxScrollTop() >= target) {
            return;
        }

        let frame = 0;
        const deadline = performance.now() + RESTORE_DEADLINE_MS;

        const stop = () => {
            cancelAnimationFrame(frame);
            USER_SCROLL_EVENTS.forEach((event) => window.removeEventListener(event, stop));
        };

        const attempt = () => {
            if (maxScrollTop() >= target) {
                window.scrollTo(0, target);
                stop();
                return;
            }
            if (performance.now() >= deadline) {
                stop();
                return;
            }
            frame = requestAnimationFrame(attempt);
        };

        USER_SCROLL_EVENTS.forEach((event) =>
            window.addEventListener(event, stop, { passive: true }),
        );
        frame = requestAnimationFrame(attempt);

        return stop;
        // The router state holds the offset for the location that `key` identifies.
    }, [key, router]);
};

/**
 * Resets the document scroll on a new navigation and restores the saved offset on
 * back/forward.
 *
 * Navigations that only rewrite search params opt out with preventScrollReset, so
 * filtering, sorting, and paging leave the viewport alone.
 */
export const AppScrollRestoration = ({ router }: AppScrollRestorationProps): ReturnComponent => {
    useDeferredScrollRestore(router);

    return <ScrollRestoration />;
};
